import api from './api';

class PagamentoPublicoService {
  /**
   * Obtém os dados da fatura pública
   * @param {string} prestacaoId - ID da prestação
   * @param {string} token - Token de acesso
   */
  async obterFaturaPublica(prestacaoId, token) {
    try {
      const response = await api.get(`/pagamento-publico/fatura/${prestacaoId}`, {
        params: { token }
      });
      return response;
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao obter fatura:', error);
      throw new Error(error.response?.data?.message || 'Erro ao obter dados da fatura');
    }
  }

  /**
   * Lista os métodos de pagamento disponíveis
   * @param {string} prestacaoId - ID da prestação
   * @param {string} token - Token de acesso
   */
  async listarMetodosPagamento(prestacaoId, token) {
    try {
      const response = await api.get(
        `/pagamento-publico/fatura/${prestacaoId}/metodos-pagamento`,
        { params: { token } }
      );
      return response.data;
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao listar métodos:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erro ao obter métodos de pagamento'
      );
    }
  }

  /**
   * Processa um pagamento público com confirmação automática
   * @param {string} prestacaoId - ID da prestação
   * @param {string} token - Token de acesso
   * @param {Object} dadosPagamento - Dados do pagamento
   */
  async processarPagamento(prestacaoId, token, dadosPagamento) {
    try {
      console.log('Processando pagamento:', {
        prestacaoId,
        dadosPagamento
      });
  
      // Validar dados antes de enviar
      const dadosValidados = this.validarDadosPagamento(dadosPagamento);
  
      const response = await api.post(
        `/pagamento-publico/fatura/${prestacaoId}/pagamento`,
        dadosValidados,
        { params: { token } }
      );
  
      return response.data;
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao processar pagamento:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erro ao processar pagamento'
      );
    }
  }
  /**
   * Confirma um pagamento pendente
   * @param {string} pagamentoId - ID do pagamento
   * @param {string} token - Token de acesso
   */
  async confirmarPagamento(pagamentoId, token) {
    try {
      const response = await api.put(
        `/pagamento-publico/pagamento/${pagamentoId}/confirmar`,
        {}, // corpo vazio pois só precisamos do ID e token
        { params: { token } }
      );
      return response.data;
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao confirmar pagamento:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erro ao confirmar pagamento'
      );
    }
  }

  /**
   * Verifica o status de um pagamento
   * @param {string} pagamentoId - ID do pagamento
   */
  async verificarStatusPagamento(pagamentoId) {
    try {
      const response = await api.get(`/pagamento-publico/pagamento/${pagamentoId}/status`);
      return response.data;
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao verificar status:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erro ao verificar status do pagamento'
      );
    }
  }

  /**
   * Valida o token de acesso
   * @param {string} token - Token de acesso
   */
  async validarToken(token) {
    try {
      const response = await api.get(`/pagamento-publico/validar-token`, {
        params: { token }
      });
      return response.data;
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao validar token:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erro ao validar token'
      );
    }
  }

  /**
   * Inicia verificação periódica do status do pagamento
   * @param {string} pagamentoId - ID do pagamento
   * @param {Object} callbacks - Funções de callback
   * @param {Object} config - Configurações da verificação
   */
  iniciarVerificacaoPeriodica(
    pagamentoId,
    {
      onSuccess,
      onError,
      onTimeout,
      onStatusChange
    },
    config = {
      intervalo: 10000, // 10 segundos
      maxTentativas: 12  // 2 minutos total
    }
  ) {
    if (!pagamentoId) {
      console.error('ID do pagamento não fornecido para verificação periódica');
      onError?.(new Error('ID do pagamento não fornecido'));
      return () => {};
    }

    let tentativas = 0;
    console.log(`[PagamentoPublicoService] Iniciando verificação periódica para pagamento ${pagamentoId}`);

    const verificador = setInterval(async () => {
      try {
        const response = await this.verificarStatusPagamento(pagamentoId);
        
        onStatusChange?.(response.status);
        
        if (response.status === 'CONFIRMADO') {
          clearInterval(verificador);
          onSuccess?.(response);
          return;
        }
        
        if (response.status === 'REJEITADO') {
          clearInterval(verificador);
          onError?.(new Error(response.mensagem || 'Pagamento rejeitado'));
          return;
        }
        
        tentativas++;
        console.log(`Tentativa ${tentativas} de ${config.maxTentativas}`);
        
        if (tentativas >= config.maxTentativas) {
          clearInterval(verificador);
          onTimeout?.();
        }
      } catch (error) {
        console.error('Erro na verificação:', error);
        clearInterval(verificador);
        onError?.(error);
      }
    }, config.intervalo);

    return () => clearInterval(verificador);
  }

  /**
   * Faz download do comprovante de pagamento
   * @param {string} pagamentoId - ID do pagamento
   */
  async downloadComprovante(pagamentoId) {
    try {
      const response = await api.get(
        `/pagamento-publico/pagamento/${pagamentoId}/comprovante`,
        { responseType: 'blob' }
      );
      return {
        data: response.data,
        url: window.URL.createObjectURL(new Blob([response.data]))
      };
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao baixar comprovante:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erro ao baixar comprovante'
      );
    }
  }

  /**
   * Faz download da fatura
   * @param {string} prestacaoId - ID da prestação
   * @param {string} token - Token de acesso
   */
  async downloadFatura(prestacaoId, token) {
    try {
      const response = await api.get(
        `/pagamento-publico/fatura/${prestacaoId}/download`,
        {
          params: { token },
          responseType: 'blob'
        }
      );
      return {
        data: response.data,
        url: window.URL.createObjectURL(new Blob([response.data]))
      };
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao baixar fatura:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erro ao baixar fatura'
      );
    }
  }

  /**
   * Valida dados de pagamento conforme o método
   * @param {Object} dadosPagamento - Dados a serem validados
   */
  validarDadosPagamento(dadosPagamento) {
    if (!dadosPagamento.metodoPagamento) {
      throw new Error('Método de pagamento é obrigatório');
    }
  
    if (!dadosPagamento.valorPago || dadosPagamento.valorPago <= 0) {
      throw new Error('Valor do pagamento inválido');
    }
  
    const dados = dadosPagamento.dadosPagamentoJSON;
    
    switch (dadosPagamento.metodoPagamento) {
      case 'MPESA':
      case 'EMOLA': {
        if (!dados?.numeroTelefone) {
          throw new Error('Número de telefone é obrigatório');
        }
        const numeroLimpo = dados.numeroTelefone.toString().replace(/\D/g, '');
        if (!numeroLimpo.match(/^(84|85|86|87)[0-9]{7}$/)) {
          throw new Error('Número de telefone inválido');
        }
        return {
          ...dadosPagamento,
          dadosPagamentoJSON: { numeroTelefone: numeroLimpo }
        };
      }
  
      case 'BCI':
      case 'STANDARDBANK':
      case 'MOZABANK': {
        if (!dados?.entidade || !dados?.referencia) {
          throw new Error('Entidade e referência são obrigatórios');
        }
        if (!dados.entidade.match(/^\d{5}$/)) {
          throw new Error('Entidade deve ter 5 dígitos');
        }
        if (!dados.referencia.match(/^\d{9}$/)) {
          throw new Error('Referência deve ter 9 dígitos');
        }
        return dadosPagamento;
      }
  
      default:
        throw new Error('Método de pagamento não suportado');
    }
  }
  /**
   * Valida o acesso do estudante
   * @param {string} prestacaoId - ID da prestação
   * @param {string} token - Token de acesso
   */
  async validarAcessoEstudante(prestacaoId, token) {
    try {
      const response = await api.get(`/pagamento-publico/validar-acesso-estudante/${prestacaoId}`, {
        params: { token }
      });
      return response.data;
    } catch (error) {
      console.error('[PagamentoPublicoService] Erro ao validar acesso do estudante:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erro ao validar acesso do estudante'
      );
    }
  }
}

export default new PagamentoPublicoService();
